<template>
  <b-container>
    <div v-if="loadError" class="text-danger organisation-load-error">{{$t('organisation_load_error')}}
      <b-button class="organisation-load-retry" type="button" :disabled="isBusy" @click="loadData">{{$t('retry')}}</b-button>
    </div>
    <span v-if="dataLoaded">
      <ValidationObserver ref="organisationCreateForm" v-slot="{ invalid }">
      <b-form
          @submit.prevent="onSubmit"
      >
        <fieldset :disabled="saving || isBusy">
          <b-form-group
              label-for="organisation"
              :label="$t('organisation.create.organisation')"
          >
            <b-form-select
                id="organisation"
                v-model="form.parentId"
                placeholder="organisation"
                :options="organisationList"
                :disabled="isAllFieldsEditAvailable()"
                value-field="id"
                text-field="name"
            />
          </b-form-group>
          <b-form-group
              label-for="name"
              :label="$t('organisation.create.type')"
          >
        <b-form-select
            id="type"
            :options="organisationTypeList"
            value-field="id"
            text-field="name"
            v-model="form.type"
            :disabled="isAllFieldsEditAvailable()"
            :placeholder="$t('organisation.create.type.placeholder')"
        >
            </b-form-select>

          </b-form-group>
            <validation-provider vid="name" v-slot="{ errors }" :rules="allFieldsEditAvailable && form.name !== initialForm.name ? 'required|unicodeLength:5,255|userName' : ''" >

          <b-form-group
              label-for="name"
              :label="$t('organisation.create.name')"
          >
            <b-input
                id="name"
                :disabled="isAllFieldsEditAvailable()"
                v-model="form.name"
                :placeholder="$t('organisation.create.password.name')"
            >
            </b-input>
            <span class="text-danger">{{ errors[0] }}</span>
          </b-form-group>
               </validation-provider>
        <b-form-group>
          <b-form-checkbox
              v-model="form.isActive"
          >
            {{$t('organisation.update.is_active')}}
          </b-form-checkbox>
        </b-form-group>
        <div v-if="responseError" class="text-danger organisation-save-error">
          {{$t('saved_error')}} <br>
          {{responseErrorMessage}}
        </div>
        <span v-if="updated">
        <div>
          {{$t('saved_successful')}}
        </div>
      </span>

        <b-button
            :disabled="invalid || saving || isBusy || loadError"
            type="submit"
            variant="primary"
        >{{$t('organisation.update')}}</b-button>
        </fieldset>
      </b-form>
    </ValidationObserver>
    </span>
  </b-container>
</template>

<script>
import axios from "axios";
import { saveForm } from "../../utils/SaveForm";
import { isOrganisationUpdateValid, organisationUpdatePayload } from "../../utils/OrganisationForm";
import { isRouteId } from "../../utils/FormValidation";
import { REQUEST_TIMEOUT_MS } from "../../utils/RequestGuard";

export default {
  name: "Edit",
  props: {
    id: { required: true},
  },
  data() {
    return {
      isBusy: true, loadError: false, saving: false, disposed: false, requestVersion: 0, readVersion: 0,
      initialForm: {},
      dataLoaded: false,
        organisationList: [],
        organisationTypeList: [],
        userTypeList: [
            {id: 'admin', name: 'admin'},
            {id: 'user', name: 'user'}
        ],
      form: {
          parentId: null,
          type: null,
          name: null,
        isActive: null
      },
      responseError: false,
      responseErrorMessage: null,
      updated: false,
        allFieldsEditAvailable: false
    }
  },
  methods: {
    onSubmit() {
      if (this.isBusy || this.loadError || !this.dataLoaded || !isRouteId(Number(this.id))) return;
      const payload = organisationUpdatePayload(this.form, this.initialForm, this.allFieldsEditAvailable);
      return saveForm(this, { method: 'post', url: 'api/v1/organisations/' + this.id, payload,
        observer: 'organisationCreateForm', success: 'updated', event: 'onUpdated', fields: ['parentId', 'type', 'name', 'isActive'],
        validPayload: value => isOrganisationUpdateValid(value, this.organisationList.map(item => item.id), this.organisationTypeList.map(item => item.id)),
      });
    },
    async loadData() {
      if (this.disposed) return;
      const request = ++this.readVersion;
      this.requestVersion++; this.saving = false; this.isBusy = true; this.loadError = false;
      this.dataLoaded = false; this.updated = false; this.responseError = false;
      const id = Number(this.id);
      try {
        if (!isRouteId(id)) throw new Error('Invalid organisation id');
        const requests = [axios.get('api/v1/organisations/' + id, { timeout: REQUEST_TIMEOUT_MS })];
        if (this.allFieldsEditAvailable) requests.push(
          axios.get('api/v1/users/organisations', { timeout: REQUEST_TIMEOUT_MS }),
          axios.get('api/v1/users/oranisationTypes', { timeout: REQUEST_TIMEOUT_MS }),
        );
        const [card, parents, types] = await Promise.all(requests);
        if (this.disposed || request !== this.readVersion) return;
        const organisation = card.data;
        if (!organisation || typeof organisation.name !== 'string' || typeof organisation.isActive !== 'boolean'
          || !isRouteId(organisation.type?.id) || !isRouteId(organisation.parent?.id)) throw new Error('Invalid organisation card');
        if (this.allFieldsEditAvailable && (!Array.isArray(parents.data?.rows) || !Array.isArray(types.data?.rows))) throw new Error('Invalid organisation dictionaries');
        this.organisationList = this.allFieldsEditAvailable ? parents.data.rows.filter(item => isRouteId(item?.id) && item.id !== id) : [organisation.parent];
        this.organisationTypeList = this.allFieldsEditAvailable ? types.data.rows.filter(item => Number.isInteger(item?.id) && item.id >= 2 && item.id <= 4) : [organisation.type];
        if (!this.organisationList.some(item => item.id === organisation.parent.id)) this.organisationList.push(organisation.parent);
        if (!this.organisationTypeList.some(item => item.id === organisation.type.id)) this.organisationTypeList.push(organisation.type);
        this.form = { parentId: organisation.parent.id, type: organisation.type.id, name: organisation.name, isActive: organisation.isActive };
        this.initialForm = { ...this.form }; this.dataLoaded = true;
      } catch (error) {
        if (!this.disposed && request === this.readVersion && error.response?.status !== 401) this.loadError = true;
      } finally { if (!this.disposed && request === this.readVersion) this.isBusy = false; }
    },
    isAllFieldsEditAvailable() {
        return !this.allFieldsEditAvailable;
    },
    validate() {
      this.$refs.organisationCreateForm.validate();
    },
      initFutureAvailable() {
          let futureAvailable = {};

          if (localStorage.getItem('futureAvailable')) {
              try {
                  futureAvailable = JSON.parse(localStorage.getItem('futureAvailable'));
              } catch(e) {
                  localStorage.removeItem('futureAvailable');
                  return;
              }
          }

          // console.log(futureAvailable)

          this.allFieldsEditAvailable = futureAvailable?.allOrganisationsEdit === true;
      },
  },
  mounted() { this.initFutureAvailable(); this.loadData(); },
  watch: { id() { this.requestVersion++; this.readVersion++; this.dataLoaded = false; this.saving = false; this.loadData(); } },
  beforeDestroy() { this.disposed = true; this.requestVersion++; this.readVersion++; },
}
</script>

<style scoped>

</style>